<template>
  <themed-modal
    name="client-modal"
    :title="isEditMode ? 'Edit Client' : 'New Client'"
    size="md"
    @closed="handleClosed"
  >
    <div v-if="!dataLoaded" class="flex items-center justify-center py-8">
      <theme-icon icon="loading" type="icons" classes="w-8 h-8 animate-spin" />
    </div>

    <themed-form
      v-else
      :fields="formFields"
      :model-value="localClient"
      :errors="errors"
      layout="grid"
      :columns="4"
      @input="localClient = $event"
    />

    <!-- Chatbot settings (KAN-185): admin-only, edit mode only -->
    <chat-admin-settings
      v-if="dataLoaded && isEditMode && $gate && $gate.isAdmin && $gate.isAdmin()"
      class="mt-4"
      :initial="localClient"
      @change="onChatbotChange"
    />

    <template #footer>
      <div class="flex justify-end gap-3">
        <themed-button
          variant="outline"
          @click="hideModal"
          :disabled="loading"
        >
          Cancel
        </themed-button>
        <themed-button
          @click="save"
          :disabled="loading || !dataLoaded"
        >
          {{ loading ? 'Saving...' : (isEditMode ? 'Save Client' : 'Create Client') }}
        </themed-button>
      </div>
    </template>
  </themed-modal>
</template>

<script>
import ThemedModal from '../ThemedModal.vue';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'ClientModal',

  components: {
    ThemedModal,
    ThemedForm,
    ThemedButton,
    ThemeIcon
  },

  props: {
    client: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'create',
      validator: (v) => ['create', 'edit'].includes(v)
    }
  },

  data() {
    return {
      localClient: {
        name: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country_id: 16 // Default to USA
      },
      countries: [],
      states: [],
      errors: {},
      loading: false,
      dataLoaded: false
    };
  },

  computed: {
    isEditMode() {
      return this.mode === 'edit';
    },

    formFields() {
      if (!this.dataLoaded) {
        return [];
      }

      return [
        {
          name: 'name',
          label: 'Name',
          type: 'input',
          required: true,
          col: 4
        },
        {
          name: 'address',
          label: 'Address',
          type: 'input',
          required: true,
          col: 4
        },
        {
          name: 'city',
          label: 'City',
          type: 'input',
          required: true,
          col: 1
        },
        {
          name: 'state',
          label: 'State',
          type: 'select',
          required: true,
          col: 1,
          options: this.stateOptions
        },
        {
          name: 'postal_code',
          label: 'Postal Code',
          type: 'input',
          required: true,
          col: 1
        },
        {
          name: 'country_id',
          label: 'Country',
          type: 'select',
          required: true,
          col: 1,
          options: this.countryOptions
        }
      ];
    },

    countryOptions() {
      return this.countries.map(country => ({
        label: country.name,
        value: country.id
      }));
    },

    stateOptions() {
      return this.states.map(state => ({
        label: state.state,
        value: state.state
      }));
    }
  },

  watch: {
    client: {
      immediate: true,
      handler(newClient) {
        if (newClient && this.isEditMode) {
          this.localClient = { ...newClient };
        } else if (!this.isEditMode) {
          // Reset for create mode
          this.localClient = {
            name: '',
            address: '',
            city: '',
            state: '',
            postal_code: '',
            country_id: 16
          };
        }
      }
    },
    
    mode: {
      immediate: true,
      handler(newMode) {
        if (newMode === 'create') {
          this.localClient = {
            name: '',
            address: '',
            city: '',
            state: '',
            postal_code: '',
            country_id: 16
          };
        } else if (newMode === 'edit' && this.client) {
          this.localClient = { ...this.client };
        }
      }
    }
  },

  mounted() {
    this.fetchAddressInfo();
  },

  methods: {
    async fetchAddressInfo() {
      try {
        const response = await axios.get('/api/address-info');
        this.countries = response.data.countries || [];
        this.states = response.data.states || [];
        this.dataLoaded = true;
      } catch (error) {
        console.error('Failed to fetch address info:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to load address information'
        });
      }
    },

    async save() {
      this.errors = {};
      this.loading = true;

      try {
        const url = this.isEditMode 
          ? `/clients/${this.localClient.id}` 
          : '/clients';
        
        const method = this.isEditMode ? 'put' : 'post';
        
        const response = await axios[method](url, this.localClient);

        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message || `Client ${this.isEditMode ? 'updated' : 'created'} successfully`
        });

        this.$emit('saved', response.data.client);
        this.hideModal();

        // Reload page if creating new client
        if (!this.isEditMode) {
          setTimeout(() => {
            window.location.href = `/clients/${response.data.client.id}`;
          }, 500);
        }
      } catch (error) {
        this.loading = false;

        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: error.response?.data?.message || `Failed to ${this.isEditMode ? 'update' : 'create'} client`
          });
        }
      }
    },

    hideModal() {
      this.$modal.hide('client-modal');
    },

    /**
     * Merge chatbot field changes (from <chat-admin-settings>) into the
     * local client model so they get sent with the existing PUT /clients/{id}
     * payload. The columns exist on the clients table; no separate endpoint.
     */
    onChatbotChange(form) {
      this.localClient = {
        ...this.localClient,
        chatbot_enabled: !!form.chatbot_enabled,
        chatbot_overpay_threshold: form.chatbot_overpay_threshold,
        chatbot_underutilized_threshold: form.chatbot_underutilized_threshold,
      };
    },

    handleClosed() {
      // Only clear data if in create mode
      if (this.mode === 'create') {
        this.localClient = {
          name: '',
          address: '',
          city: '',
          state: '',
          postal_code: '',
          country_id: 16
        };
      }
      this.errors = {};
      this.loading = false;
    }
  }
};
</script>
